import React, { forwardRef } from 'react';

import { useIntersectionObserver } from '@trqk/hooks';

import { ScrollButtonContent, SlideWrapper } from './components';

/**
 * @typedef {CarouselProps}
 * @property {Slide[]} slides array of slide objects to display in Carousel
 */
interface CarouselProps {
    slides: Slide[];
}

/**
 * Displays a series of slides in div that can scroll horizontally. Two buttons are provided that
 * allow the user to scroll forward to the next slide or back to the previous slide.
 * @param {CarouselProps} props
 */
export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
    ({ slides }, ref): JSX.Element => {
        const observerRoot =
            typeof ref === 'function' ? null : ref?.current ?? null;

        const { intersections, observe, scrollToSectionId } =
            useIntersectionObserver({
                defaultSectionId: slides?.[0]?.id ?? '',
                sectionIds: slides.map(({ id }: Slide): string => id),
                observerOptions: {
                    root: observerRoot,
                    rootMargin: '0px',
                    threshold: 1,
                },
                scrollIntoViewOptions: {
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest',
                },
            });

        let prevId: Maybe<string> = null;
        let nextId: Maybe<string> = null;

        // Find the ids of the items that are immediately previous to the first visible item and
        // immediately next after the last visible item. These are used to provide scrolling
        // functionality via the left / right buttons.
        intersections.forEach(
            (intersection: Intersection, index: number): void => {
                const prevIntersection =
                    index > 0 ? intersections[index - 1] : null;
                const nextIntersection =
                    index < intersections.length - 1
                        ? intersections[index + 1]
                        : null;

                if (prevIntersection !== null && prevId === null) {
                    if (
                        prevIntersection.isIntersecting === false &&
                        intersection.isIntersecting
                    ) {
                        prevId = prevIntersection.id;
                    }
                }

                if (nextIntersection !== null && nextId === null) {
                    if (
                        nextIntersection.isIntersecting === false &&
                        intersection.isIntersecting
                    ) {
                        nextId = nextIntersection.id;
                    }
                }
            },
        );

        return (
            <>
                <div ref={ref} className="flex w-full overflow-x-auto">
                    {slides.map(
                        ({ content, id }: Slide): JSX.Element => (
                            <SlideWrapper key={id} id={id} ref={observe}>
                                {content}
                            </SlideWrapper>
                        ),
                    )}
                </div>
                <div className="flex justify-end mt-2">
                    <div>
                        <button
                            type="button"
                            className="mx-2"
                            onClick={(): void => {
                                if (prevId) {
                                    scrollToSectionId(prevId);
                                }
                            }}
                        >
                            <ScrollButtonContent
                                disabled={prevId === null}
                                direction="left"
                            />
                        </button>
                        <button
                            type="button"
                            className="mx-2"
                            onClick={(): void => {
                                if (nextId) {
                                    scrollToSectionId(nextId);
                                }
                            }}
                        >
                            <ScrollButtonContent
                                disabled={nextId === null}
                                direction="right"
                            />
                        </button>
                    </div>
                </div>
            </>
        );
    },
);
